/* Presentations — the Editorial slide types, as SHAPES.
 *
 * Step 1 of docs/presentations-spec.md (her go-ahead, 2026-09-16): prove the
 * .pptx before anything is built around it.
 *
 * ONE LAYOUT, TWO RENDERERS. Each slide type is described once here as a list
 * of positioned shapes — rectangles, lines and text boxes in INCHES on a
 * 13.333 × 7.5 widescreen slide, with colours already resolved from her skin.
 * `deckExport.js` turns those shapes into PowerPoint; a preview component can
 * turn the SAME list into positioned boxes on screen (her ask: "the preview is
 * key"). Neither renderer decides anything about layout, so the preview and
 * the file cannot drift apart — the spec's own requirement.
 *
 * Pure: no React, no pptxgenjs, no network. Tested in deckLayout.test.js.
 */

export const SLIDE_W = 13.333;
export const SLIDE_H = 7.5;

/* ── The skin ────────────────────────────────────────────────────────────────
   Roles, never raw brand values, so any coach's locked palette can wear the
   same structure. Hex strings WITHOUT the '#', which is what pptxgenjs wants;
   the preview adds it back. */
export const SKIN_ROLES = ["bgPrimary", "bgDark", "brandPrimary", "brandAccent", "textOnLight", "textOnDark", "fontHeading", "fontBody"];

const hex = (v) => {
  const s = String(v || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(s)) return s.split("").map((c) => c + c).join("").toUpperCase();
  return /^[0-9a-f]{6}$/i.test(s) ? s.toUpperCase() : null;
};

function rgb(h) {
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

/** Relative luminance, WCAG. */
export function luminance(h) {
  const [r, g, b] = rgb(h).map((c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

/** A colour mixed toward black (amount > 0) or white (amount < 0). */
export function shade(h, amount) {
  const target = amount > 0 ? 0 : 255;
  const t = Math.abs(amount);
  return rgb(h).map((c) => Math.round(c + (target - c) * t).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** Grey of the same lightness — the "Old Way" half's muted treatment. */
export function desaturate(h) {
  const [r, g, b] = rgb(h);
  const y = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  return [y, y, y].map((c) => c.toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** The text colour that reads on `bg`: the preferred one if it passes 4.5:1,
 *  else whichever of near-black / white reads better. Contrast is enforced,
 *  never left to the palette (the spec's rule). */
export function readableOn(bg, preferred) {
  if (preferred && contrast(bg, preferred) >= 4.5) return preferred;
  return contrast(bg, "1A1A1A") >= contrast(bg, "FFFFFF") ? "1A1A1A" : "FFFFFF";
}

/**
 * Fill in a skin from whatever roles are known, and say what was filled in.
 *
 * A missing dark base is a DARKER TINT OF HER PRIMARY, never Brill's own teal
 * (the spec). With no primary at all there is nothing of hers to tint, so the
 * fallback is a neutral charcoal and `missing` says so — a caller shows that,
 * because a deck quietly wearing someone else's colours is the silent failure.
 */
export function resolveSkin(input = {}) {
  const missing = SKIN_ROLES.filter((r) => (r.startsWith("font") ? !String(input[r] || "").trim() : !hex(input[r])));
  const brandPrimary = hex(input.brandPrimary) || "3A3A3A";
  const bgPrimary = hex(input.bgPrimary) || "FAF8F5";
  const bgDark = hex(input.bgDark) || shade(brandPrimary, 0.6);
  const brandAccent = hex(input.brandAccent) || brandPrimary;
  return {
    bgPrimary, bgDark, brandPrimary, brandAccent,
    textOnLight: readableOn(bgPrimary, hex(input.textOnLight)),
    textOnDark: readableOn(bgDark, hex(input.textOnDark)),
    fontHeading: String(input.fontHeading || "").trim() || "Georgia",
    fontBody: String(input.fontBody || "").trim() || "Arial",
    missing,
  };
}


/* ── The slide types ──────────────────────────────────────────────────────────
   `fields` is what each type holds — the content a slide carries, and what
   Brill will be asked for. No slide carries more than one job. */
/* Twelve since 2026-10-01 — her ask, "we'll need more options with pictures":
   a full picture, the split flipped, and a row of two or three. Title and
   Quote take an OPTIONAL picture (a background, a headshot) and are drawn as
   before without one. */
export const SLIDE_TYPES = [
  { id: "title", label: "Title", fields: ["headline", "subtitle", "presenter", "image"] },
  { id: "divider", label: "Section Divider", fields: ["number", "title"] },
  { id: "statement", label: "Single-Point Statement", fields: ["line"] },
  { id: "list", label: "List / Framework", fields: ["heading", "items"] },
  { id: "image_text", label: "Image + Text Split", fields: ["heading", "body", "image"] },
  { id: "text_image", label: "Text + Image Split", fields: ["heading", "body", "image"] },
  { id: "image_full", label: "Full Picture", fields: ["image", "heading", "line"] },
  { id: "gallery", label: "Picture Row", fields: ["heading", "image1", "caption1", "image2", "caption2", "image3", "caption3"] },
  { id: "quote", label: "Quote / Pull-Out", fields: ["quote", "attribution", "photo"] },
  { id: "stat", label: "Stat Callout", fields: ["number", "line"] },
  { id: "old_new", label: "Old Way / New Way", fields: ["pairs"] },
  { id: "closing", label: "Closing / CTA", fields: ["headline", "line", "cta", "link"] },
];

export function slideTypeDef(id) {
  return SLIDE_TYPES.find((t) => t.id === id) || null;
}

const str = (v) => String(v ?? "").trim();
const M = 0.8;   // the outer margin, inches

/* Shape helpers. Every shape is plain data. */
const rect = (x, y, w, h, fill, extra = {}) => ({ kind: "rect", x, y, w, h, fill, ...extra });
const line = (x, y, w, color, weight = 2) => ({ kind: "line", x, y, w, h: 0, color, weight });
const text = (value, x, y, w, h, o) => {
  const sh = { kind: "text", text: value, x, y, w, h, align: "left", valign: "top", bold: false, ...o };
  // A decorative glyph (the quote slide's big quotation mark) keeps its size.
  return sh.noFit ? sh : { ...sh, fontSize: fitFont(sh.text, w, h, sh.fontSize, sh.bold) };
};

/* WORDS NEVER RUN OUT OF THEIR BOX. Her report, 2026-10-01: a long heading on
   a Picture Row wrapped to a second line that sat on the rule under it. The
   preview cannot shrink text and PowerPoint's own "shrink on overflow" only
   acts once the text is edited, so the size is decided HERE — the one place
   both renderers read — by wrapping the words at an estimated character
   width and stepping the size down until the lines fit. The estimate leans
   wide (bold wider still) so a fit here is a fit on screen. */
export const LINE_HEIGHT = 1.15;

/* Each character's width in ems, leaning wide, and only 92% of the line
   counted as usable. Measured in the browser on 2026-10-01 against Georgia,
   Arial, Poppins, Lexend, Montserrat, Playfair Display and Verdana, bold and
   regular, 14–120pt, real headings and all-caps and W/M stress lines: 1,134
   cases, none where a real line ran longer than this says. Counting
   characters alone under-counted ALL-CAPS lines and Lexend, her body font. */
function charEm(ch) {
  if (ch === " ") return 0.3;
  if ("WM".includes(ch)) return 1.0;
  if ("mw".includes(ch)) return 0.86;
  if ("iljtfrI.,:;'!|()\"’‘".includes(ch)) return 0.36;
  if (/[A-Z]/.test(ch)) return 0.8;
  if (/[0-9]/.test(ch)) return 0.62;
  return 0.58;
}

export function linesAt(value, w, size, bold) {
  const room = (0.92 * w * 72) / (size * (bold ? 1.1 : 1));   // the line's usable width in ems
  const em = (s) => [...s].reduce((m, ch) => m + charEm(ch), 0);
  let lines = 0;
  for (const para of String(value ?? "").split("\n")) {
    let used = 0;
    let n = 1;
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const ww = em(word);
      if (used > 0 && used + charEm(" ") + ww <= room) { used += charEm(" ") + ww; continue; }
      if (used > 0) n += 1;
      // A word longer than a line breaks across lines, as the browser does.
      n += Math.max(0, Math.ceil(ww / room) - 1);
      used = ww > room ? ww % room : ww;
    }
    lines += n;
  }
  return lines;
}

export function fitFont(value, w, h, size, bold) {
  if (!size || !String(value ?? "").trim()) return size;
  const floor = Math.max(10, Math.round(size * 0.4));
  let s = size;
  while (s > floor && linesAt(value, w, s, bold) * s * LINE_HEIGHT > h * 72) s -= 1;
  return s;
}
/* A picture, or the PLACEHOLDER PICTURE where none is chosen — a real picture,
   not a box, because that is what PowerPoint's "Change Picture" and Google
   Slides' "Replace image" act on, and both keep its size and place (her ask,
   2026-09-16: "easy to replace in pptx"). Both renderers crop it to fill its
   slot (cover), never stretch it. */
const PLACEHOLDER_ALT = "Image placeholder — right-click and choose Change Picture (PowerPoint) or Replace image (Google Slides)";
const picture = (src, x, y, w, h, fill, extra = {}) => (str(src)
  ? { kind: "image", src: str(src), x, y, w, h, ...extra }
  : { kind: "image", placeholder: true, fill, x, y, w, h, alt: PLACEHOLDER_ALT, ...extra });

/** How many pictures a Picture Row shows: three, or two when the third slot is
 *  empty and an earlier one is not. Empty throughout, it offers all three. */
export function galleryCount(c = {}) {
  const used = (n) => !!(str(c[`image${n}`]) || str(c[`caption${n}`]));
  return !used(3) && (used(1) || used(2)) ? 2 : 3;
}

/* ── Designs ─────────────────────────────────────────────────────────────────
   Her spec's "template dimension", and her note 2026-09-16: "a couple different
   types of designs for different industries, health, business etc". A design
   is ONE function from (slide, skin) to shapes. Every design takes the same
   slide types and the same content, so switching design never touches what she
   wrote — a Health or Business design is another entry here, not a rebuild. */
export const DESIGNS = [
  { id: "editorial", label: "Editorial", hint: "Big type, generous space, warm authority" },
];

export function layoutSlide(slide, skin, design = "editorial") {
  return (DESIGN_LAYOUTS[design] || DESIGN_LAYOUTS.editorial)(slide, skin);
}

/**
 * One slide as shapes, plus its background and speaker notes — EDITORIAL.
 * @returns { background, shapes: [...], notes }
 */
function layoutEditorial(slide, skin) {
  const s = skin;
  const c = slide?.content || {};
  const heading = (value, x, y, w, h, size, color, o = {}) => text(value, x, y, w, h, { fontFace: s.fontHeading, fontSize: size, color, bold: true, ...o });
  const body = (value, x, y, w, h, size, color, o = {}) => text(value, x, y, w, h, { fontFace: s.fontBody, fontSize: size, color, ...o });
  const out = (background, shapes) => ({ background, shapes: shapes.filter(Boolean), notes: str(slide?.notes) });

  switch (slide?.type) {
    case "title":
      // Full-bleed dark; headline large and left in the lower third; a thin
      // accent rule above the subtitle; her name in the accent colour.
      // A BACKGROUND PICTURE, when she gives one, fills the slide under a dark
      // veil over the lower part, where the words sit — the top stays clear,
      // so the picture is seen and is still the thing a click lands on.
      return out(s.bgDark, [
        str(c.image) && picture(c.image, 0, 0, SLIDE_W, SLIDE_H),
        str(c.image) && rect(0, 2.9, SLIDE_W, SLIDE_H - 2.9, s.bgDark, { transparency: 22 }),
        heading(str(c.headline), M, 3.35, SLIDE_W * 0.66, 1.9, 48, s.textOnDark, { valign: "bottom" }),
        line(M, 5.45, 1.6, s.brandAccent, 3),
        body(str(c.subtitle), M, 5.6, SLIDE_W * 0.6, 0.6, 20, s.textOnDark),
        str(c.presenter) && body(str(c.presenter), M, 6.3, SLIDE_W * 0.6, 0.45, 14, readableOn(s.bgDark, s.brandAccent), { bold: true }),
      ]);

    case "divider": {
      // Full-bleed primary; an oversized number top-left; the title
      // centred-left. No body copy.
      const bg = s.brandPrimary;
      const ink = readableOn(bg, s.textOnDark);
      return out(bg, [
        heading(str(c.number), M, 0.5, 4, 2.6, 120, ink),
        heading(str(c.title), M, 3.2, SLIDE_W * 0.66, 1.6, 44, ink, { valign: "middle" }),
      ]);
    }

    case "statement":
      // One short line, very large, centred vertically. Nothing else.
      return out(s.bgPrimary, [
        heading(str(c.line), M * 1.5, 1.5, SLIDE_W - M * 3, 4.5, 44, s.textOnLight, { valign: "middle" }),
      ]);

    case "list": {
      // Heading top-left; 3–5 items down the left two-thirds; the right third
      // left as breathing room.
      const items = (Array.isArray(c.items) ? c.items : []).slice(0, 5);
      const top = 2.1;
      const step = Math.min(1.05, (SLIDE_H - top - 0.6) / Math.max(items.length, 1));
      const width = SLIDE_W * 0.62;
      return out(s.bgPrimary, [
        heading(str(c.heading), M, 0.6, width, 1.1, 34, s.textOnLight),
        line(M, 1.8, 1.2, s.brandPrimary, 3),
        ...items.flatMap((it, i) => {
          const label = str(typeof it === "string" ? it : it?.label);
          const detail = str(typeof it === "string" ? "" : it?.detail);
          const y = top + i * step;
          return [
            heading(String(i + 1).padStart(2, "0"), M, y, 0.7, 0.5, 18, readableOn(s.bgPrimary, s.brandPrimary)),
            heading(label, M + 0.8, y, width - 0.8, 0.45, 20, s.textOnLight),
            detail && body(detail, M + 0.8, y + 0.42, width - 0.8, 0.5, 14, s.textOnLight),
          ];
        }),
      ]);
    }

    case "image_text":
    case "text_image": {
      // Image full-bleed on one half; heading and 2–3 lines on the other.
      // Image + Text puts the picture left, Text + Image right — the same
      // fields, so changing one into the other is a flip that loses nothing.
      // Nothing is laid over the picture, so a click lands on it.
      const half = SLIDE_W / 2;
      const picX = slide.type === "text_image" ? half : 0;
      const textX = (picX ? 0 : half) + M * (picX ? 1 : 0.8);
      return out(s.bgPrimary, [
        picture(c.image, picX, 0, half, SLIDE_H, shade(s.bgPrimary, 0.12)),
        heading(str(c.heading), textX, 1.6, half - M * 1.8, 1.6, 32, s.textOnLight, { valign: "bottom" }),
        line(textX, 3.4, 1.2, s.brandPrimary, 3),
        body(str(c.body), textX, 3.65, half - M * 1.8, 2.6, 18, s.textOnLight),
      ]);
    }

    case "image_full": {
      // The picture is the slide. Words, if any, sit on a dark band across the
      // bottom — the rest of the picture stays clear and clickable.
      const words = str(c.heading) || str(c.line);
      const bandY = SLIDE_H - 2.2;
      return out(s.bgDark, [
        picture(c.image, 0, 0, SLIDE_W, SLIDE_H, shade(s.bgPrimary, 0.12)),
        words && rect(0, bandY, SLIDE_W, SLIDE_H - bandY, s.bgDark, { transparency: 15 }),
        heading(str(c.heading), M, bandY + 0.3, SLIDE_W - M * 2, 0.95, 34, s.textOnDark, { valign: "bottom" }),
        body(str(c.line), M, bandY + 1.3, SLIDE_W - M * 2, 0.6, 18, s.textOnDark),
      ]);
    }

    case "gallery": {
      // A heading, then two or three pictures side by side, each with a short
      // caption under it.
      const n = galleryCount(c);
      const gap = 0.4;
      // The heading has room for two lines (her "You are not afraid of the
      // dark…" wrapped onto the rule) and shrinks past that.
      const top = str(c.heading) ? 2.05 : 1.1;
      const w = (SLIDE_W - M * 2 - gap * (n - 1)) / n;
      const h = Math.min(3.7, SLIDE_H - top - 1.3);
      return out(s.bgPrimary, [
        heading(str(c.heading), M, 0.45, SLIDE_W - M * 2, 1.1, 32, s.textOnLight, { valign: "bottom" }),
        str(c.heading) && line(M, 1.75, 1.2, s.brandPrimary, 3),
        ...Array.from({ length: n }, (_, i) => {
          const x = M + i * (w + gap);
          return [
            picture(c[`image${i + 1}`], x, top, w, h, shade(s.bgPrimary, 0.12)),
            body(str(c[`caption${i + 1}`]), x, top + h + 0.2, w, 0.9, 16, s.textOnLight),
          ];
        }).flat(),
      ]);
    }

    case "quote":
      // Dark; an oversized quotation mark as a graphic; the quote large and
      // centred; attribution small beneath. A HEADSHOT, when there is one,
      // takes the quotation mark's place — a round picture, so the room sees
      // whose words these are.
      return out(s.bgDark, [
        str(c.photo)
          ? picture(c.photo, (SLIDE_W - 1.6) / 2, 0.45, 1.6, 1.6, null, { rounding: true })
          : heading("“", 0, 0.2, SLIDE_W, 2.2, 160, readableOn(s.bgDark, s.brandAccent), { align: "center", noFit: true }),
        text(str(c.quote), M * 1.8, 2.3, SLIDE_W - M * 3.6, 3, { fontFace: s.fontHeading, fontSize: 30, color: s.textOnDark, align: "center", valign: "middle", italic: true }),
        body(str(c.attribution) ? `— ${str(c.attribution)}` : "", M, 5.6, SLIDE_W - M * 2, 0.5, 16, s.textOnDark, { align: "center" }),
      ]);

    case "stat":
      // One number, extremely large and centred; one short line beneath.
      return out(s.bgPrimary, [
        heading(str(c.number), M, 1.2, SLIDE_W - M * 2, 3.2, 120, readableOn(s.bgPrimary, s.brandPrimary), { align: "center", valign: "bottom" }),
        body(str(c.line), M * 2, 4.7, SLIDE_W - M * 4, 1, 22, s.textOnLight, { align: "center" }),
      ]);

    case "old_new": {
      // Split down the middle: left muted, "Before"; right in full colour,
      // "After"; 2–3 paired lines.
      const pairs = (Array.isArray(c.pairs) ? c.pairs : []).slice(0, 3);
      const half = SLIDE_W / 2;
      const oldBg = desaturate(shade(s.bgPrimary, 0.12));
      const newBg = s.brandPrimary;
      const oldInk = readableOn(oldBg);
      const newInk = readableOn(newBg, s.textOnDark);
      return out(s.bgPrimary, [
        rect(0, 0, half, SLIDE_H, oldBg),
        rect(half, 0, half, SLIDE_H, newBg),
        heading("Before", M, 0.7, half - M * 2, 0.8, 30, oldInk),
        heading("After", half + M, 0.7, half - M * 2, 0.8, 30, newInk),
        ...pairs.flatMap((p, i) => [
          body(str(p?.old), M, 2.1 + i * 1.5, half - M * 2, 1.2, 20, oldInk),
          body(str(p?.new), half + M, 2.1 + i * 1.5, half - M * 2, 1.2, 20, newInk, { bold: true }),
        ]),
      ]);
    }

    case "closing": {
      // Dark; headline and one line; the CTA as a bold accent block carrying
      // the link.
      const ctaInk = readableOn(s.brandAccent);
      return out(s.bgDark, [
        heading(str(c.headline), M, 1.6, SLIDE_W * 0.7, 1.8, 44, s.textOnDark, { valign: "bottom" }),
        body(str(c.line), M, 3.6, SLIDE_W * 0.6, 1, 20, s.textOnDark),
        str(c.cta) && rect(M, 5.0, 4.2, 0.9, s.brandAccent),
        str(c.cta) && text(str(c.cta), M, 5.0, 4.2, 0.9, { fontFace: s.fontBody, fontSize: 20, bold: true, color: ctaInk, align: "center", valign: "middle", link: str(c.link) || null }),
      ]);
    }

    default:
      return out(s.bgPrimary, [
        body(`Unknown slide type: ${str(slide?.type) || "(none)"}`, M, M, SLIDE_W - M * 2, 1, 18, s.textOnLight),
      ]);
  }
}

const DESIGN_LAYOUTS = { editorial: layoutEditorial };

/** The whole deck as laid-out slides. */
export function layoutDeck(slides = [], skinInput = {}, design = "editorial") {
  const skin = resolveSkin(skinInput);
  return { skin, design, slides: (slides || []).map((sl) => ({ type: sl?.type, ...layoutSlide(sl, skin, design) })) };
}
